import { Component, Input, OnInit } from '@angular/core';
import { Location, ViewportScroller } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { NavigationService } from '../../shared/services/navigation.service';

@Component({
  selector: 'app-webhook-documentation',
  templateUrl: './webhook-documentation.component.html',
  styleUrls: ['./webhook-documentation.component.scss']
})
export class WebhookDocumentationComponent implements OnInit {

  private _target: { tab: DocumentationTab, anchor: DocumentationAnchor };

  @Input()
  get target() {
    return this._target;
  }

  set target(target: { tab: DocumentationTab, anchor: DocumentationAnchor }) {
    this._target = target;
    if (target && target.tab) {
      this.displayTab(target.tab);
      if (target.anchor) {
        setTimeout(() => this.scrollTo(target.anchor), 100);
      }
    }
  }

  public commonWrapExample = {
    id: '0cc811f6-82d5-442c-8a0d-52a2fed2a352',
    numberOfTries: '1',
    emittedAt: '2020-11-19T13:41:18+0530',
    data: {
      // Specific event data
    }
  }

  public checkInExample = {
    deliveryReference: '3669799049',
    type: 'PICK',
    arrivalTime: '2020-11-19T13:41:18+0530',
    vehicleName: 'PRADEEP',
    location: {
      type: 'CHEP',
      reference: 'GB03',  // from glid
      name: 'GOLDCREST',
      coordinates: {
        'latitude': 51.5443183,
        'longitude': -2.6426384
      },
      address: {
        address1: 'Goldcrest Way',
        address2: null,
        address3: null,
        city: 'BRISTOL',
        state: 'AV',
        zip: 'BS35 4GH',
        country: 'GB'
      },
      timezone : 'Australia/Melbourne'
    }
  }

  public checkOutExample = {
    deliveryReference: '3669799049',
    type: 'PICK',
    departureTime: '2020-11-19T13:41:18+0530',
    vehicleName: 'PRADEEP',
    location: {
      type: 'CHEP',
      reference: 'GB03', // from glid
      coordinates: {
        latitude: 51.5443183,
        longitude: -2.6426384
      },
      address: {
        address1: 'Goldcrest Way',
        address2: null,
        address3: null,
        city: 'BRISTOL',
        state: 'AV',
        zip: 'BS35 4GH',
        country: 'GB'
      }
    },
    items: [
      {
        origin: 'PLANNED',
        productCode: '1',
        productDescription: null,
        quantity: 10,
        expectedQuantity: 10
      }
    ]
  }

  public etaUpdatedExample = {
    'deliveryReference': '3669799049',
    'estimatedArrivalTimeInterval': {
      'from': '2020-11-19T20:15:00+05:30',
      'to': '2020-11-19T20:15:00+05:30'
    },
    'confidenceScore': 2,
    'vehicleName': 'PRADEEP',
    'destinationLocation': {
      'type': 'CUST',
      'reference': '5000524044',
      'name': 'LOCKING',
      'coordinates': {
        'latitude': 51.356545,
        'longitude': -2.897586
      },
      'address': {
        'address1': 'LOCKING CASTLE BUSINESS PARK, WESTW',
        'address2': '01',
        'address3': null,
        'city': 'WESTON-SUPER-MARE',
        'state': null,
        'zip': 'BS24 7TG',
        'country': 'GB'
      },
      'timezone' : 'Australia/Melbourne'
    },
    'driverCoordinates': {
      'latitude': 45.519262,
      'longitude': -73.6169577
    }
  };


  public bolUpdatedExample = {
    deliveryReference: '3669799049',
    downloadUrl: 'https://cma-api-prd-ng.azurewebsites.net/shipment_tracking_rest/api/documents/download?token=ec60396f-5a2b-4fda-aa19-d9923741dd6f',
    serviceCenter: {
      type: 'CHEP',
      reference: 'AU03',  // from glid
      name : 'GILLMAN',
      coordinates: {
        latitude: 51.5443183,
        longitude: -2.6426384
      },
      timezone : 'Australia/Melbourne'
    }
  }

  public pgi = {
    deliveryReference: '3669799049',
    pgiDate: '2020-12-07T07:00:00.000Z',
    additionalInformations: {}
  }

  public failedCollection = {
    deliveryReference: '3669799046',
    failedAt: '2020-12-07T07:00:00.000',
    vehicleName: 'PRADEEP',
    location: {
      type: 'CHEP',
      name : 'GILLMAN',
      reference: 'AU03',  // from glid
      coordinates: {
        latitude: 51.5443183,
        longitude: -2.6426384
      },
      timezone : 'Australia/Melbourne'
    },
    additionalInformations: {}
  }

  public reverseCheckin = {
    deliveryReference: '3669799046',
    type: 'Pick',
    carrier: {
      name: 'MY_CARRIER',
      identifier: 'CARRIER_IDENTIFIER'
    },
    vehicleName: 'PRADEEP',
    location: {
      type: 'CHEP',
      name : 'GILLMAN',
      reference: 'AU03',  // from glid
      coordinates: {
        latitude: 51.5443183,
        longitude: -2.6426384
      },
      timezone : 'Australia/Melbourne'
    },
    reason: 'reason for reversal',
    additionalInformations: {}
  }

  public tripCancelledExample = {
    tripId: '06d02e1a-1a4S-4fSe-b0be-b726101c07cd',
    cancellationInfo: {
      reason: 'Trip cancellation reason',
      source: 'UNKNOWN'
    },
    deliveryReferences: [
      '3671684624'
    ],
    load: {
      loadId: '06d02e1a-1a4S-4fSe-b0be-b726101c07cc',
      sourceId: 'UNKNOWN',
      planning: {
        start: '2020-12-07T07:00:00.000Z',
        end: '2020-12-07T18:07:00.000Z'
      }
    },
    vehicleName: 'PRADEEP',
    carrier: {
      name: 'MY_CARRIER',
      identifier: 'CARRIER_IDENTIFIER'
    },
    additionalInformations: {}
  }

  public tripUpdatedExample = {
    tripId: '06d02e1a-1a4S-4fSe-b0be-b726101c07cd',
    updatedFields: [
      {
        field: 'LOAD_PLANNING_START',
        oldValue: '2020-12-07T07:00:00.000Z',
        newValue: '2020-12-07T09:00:00.000Z'
      },
      {
        field: 'LOAD_PLANNING_END',
        oldValue: '2020-12-07T08:00:00.000Z',
        newValue: '2020-12-07T10:00:00.000Z'
      }
    ],
    additionalInformations: {}
  }

  public tripClosedExample = {
    id: '915bbdb1-e7fd-42c0-b98a-c1b33cf3ea4f',
    numberOfTries: 0,
    emittedAt: '2023-11-27T13:55:50.792Z',
    data: {
      tripId: '02d5ad92-20c0-4236-904e-0bb459648cb4',
      deliveryReferences: [
        '3773356812'
      ],
      loadId: '420946209',
      carrier: {
        name: 'Montgomery Transport LTD',
        identifier: '345014'
      }
    }
  }

  public tripCreatedExample = {
    id: '84dee136-dc76-4fd5-8507-a88db248c8cb',
    numberOfTries: 0,
    emittedAt: '2023-09-29T08:45:51.507Z',
    data: {
      direction: 'INBOUND',
      tripId: 'f233a9e6-b1f4-4fad-9766-f6c2b17c6707',
      deliveryReferences: [
        '3773467992'
      ],
      load: {
        loadId: '421010378',
        sourceId: 'BLUEYONDER',
        planning: {
          start: '2023-02-10T13:08:00.000Z',
          end: '2023-02-10T14:00:00.000Z'
        },
        priority: 1,
        sequenceNumber: null,
        scaledWeight: 4385.08,
        serviceCode: 'TL'
      },
      originLocation: {
        type: 'CUST',
        reference: '0101031167',
        name: 'Dana UK Axles Ltd',
        coordinates: {
          latitude: 52.5163,
          longitude: -1.8783
        },
        address: {
          address1: 'BIRCH ROAD',
          address2: null,
          address3: null,
          city: 'WITTON BIRMINGHAM',
          state: '00',
          zip: 'B67JR',
          country: 'GB'
        },
        timezone: 'Europe/London',
        stopType: 'PICK',
        appointment: {
          start: '2023-02-10T13:08:00.000Z',
          end: '2023-02-10T13:08:00.000Z'
        },
        appointments: [
          {
            start: '2023-02-10T13:08:00.000Z',
            end: '2023-02-10T13:08:00.000Z',
            type: 'CALCULATED'
          }
        ],
        pickupTimeWindow: {
          start: '2023-02-10T08:00:00.000Z',
          end: '2023-02-10T17:00:00.000Z'
        },
        contactDetails: {
          name: 'Toby Roper',
          email: 'mail@test.com',
          phone: '0653416142'
        },
        externalReferences: [
          {
            key: 'TMS_LOCATION_REFERENCE',
            value: '0101031157'
          }
        ]
      },
      destinationLocation: {
        type: 'CHEP',
        reference: 'GB62',
        name: 'West Bromwich',
        coordinates: {
          latitude: 52.5117,
          longitude: -1.9785
        },
        address: {
          address1: 'ROEBUCK STREET',
          address2: null,
          address3: null,
          city: 'WEST BROMWICH',
          state: '00',
          zip: 'B706RB',
          country: 'GB'
        },
        timezone: 'Europe/London',
        stopType: 'DROP',
        appointment: {
          start: '2023-02-10T13:30:00.000Z',
          end: '2023-02-10T14:00:00.000Z'
        },
        appointments: [
          {
            start: '2023-02-10T13:30:00.000Z',
            end: '2023-02-10T14:00:00.000Z',
            type: 'CALCULATED'
          },
          {
            start: '2023-02-10T13:30:00.000Z',
            end: '2023-02-10T14:00:00.000Z',
            type: 'FIXED_BY_CARRIER'
          }
        ],
        deliveryTimeWindow: {
          start: '2023-02-10T08:02:00.000Z',
          end: '2023-02-11T23:59:00.000Z'
        },
        contactDetails: {
          name: 'Toby Roper',
          email: 'mail@test.com',
          phone: '0653426142'
        },
        externalReferences: [
          {
            key: 'TMS_LOCATION_REFERENCE',
            value: 'GB62'
          }
        ]
      },
      shipment: {
        legNumber: '3773467992',
        loadGroup: 'TRRLOADGROUP',
        loadType: 'MET',
        salesOrderNumber: '3147936036',
        items: [
          {
            productId: '0000010001',
            productDescription: 'CHEP Pallet Aust',
            quantity: 480,
            nominalWeight: 6635366
          }
        ]
      },
      loadingDurationInSeconds: 0,
      unloadingDurationInSeconds: 1800,
      vehicleName: 'TRT',
      vehicleDescription: 'Truck description',
      vehicleContactPhoneNumber: '+33 6 34 68 17 99',
      driverContactPhoneNumber: '+33 6 32 38 16 43',
      trailerDetails: {
        number: 'trailerNumber',
        equipmentTypeCode: 'SFT24',
        equipmentDescription: null
      },
      carrier: {
        name: 'Montgomery Transport LTD',
        identifier: '335014'
      },
      additionalInformations: null
    }
}

  public DocumentationAnchor = DocumentationAnchor;
  public DocumentationTab = DocumentationTab;
  public selectedTab = DocumentationTab.TAB_OBJECTIVE;

  constructor(
    private route: ActivatedRoute,
    private viewportScroller: ViewportScroller,
    private location: Location,
    private navigation: NavigationService,
  ) {
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params.tab || params.anchor) {
        this.target = { tab: params.tab, anchor: params.anchor };
      }
    });
  }

  public scrollTo(documentationAnchor: DocumentationAnchor): void {
    this.viewportScroller.scrollToAnchor(documentationAnchor);
    // Scrolling again because of fixed header
    this.viewportScroller.scrollToPosition([this.viewportScroller.getScrollPosition()[0], this.viewportScroller.getScrollPosition()[1] - 85]);
  }

  clickOnTab(documentationTab: DocumentationTab) {
    this.displayTab(documentationTab);
    this.location.go(this.navigation.webhooksEvents().doc(documentationTab).getUri());
  }

  clickOnTabItem(anchor: DocumentationAnchor) {
    this.location.go(this.navigation.webhooksEvents().doc(this.selectedTab, anchor).getUri());
    this.scrollTo(anchor);
  }

  public displayTab(documentationTab: DocumentationTab): void {
    this.selectedTab = documentationTab;
  }
}

export enum DocumentationAnchor {
  INTERFACE_CONFIGURATION = 'INTERFACE_CONFIGURATION',
  INTERFACE_EVENT = 'INTERFACE_EVENT',
  INTERFACE_EVENT_DETAILS = 'INTERFACE_EVENT_DETAILS',
  EVENTS_COMMON_WRAP = 'EVENTS_COMMON_WRAP',
  EVENTS_COMMON_DATA = 'EVENTS_COMMON_DATA',
  EVENTS_COMMON_DATA_LOCATION = 'EVENTS_COMMON_DATA_LOCATION',
  EVENTS_COMMON_DATA_TRIP_LOCATION = 'EVENTS_COMMON_DATA_TRIP_LOCATION',
  EVENTS_COMMON_DATA_LOAD_DATA = 'EVENTS_COMMON_DATA_LOAD_DATA',
  EVENTS_COMMON_DATA_FIELD_CHANGE = 'EVENTS_COMMON_DATA_FIELD_CHANGE',
  EVENTS_COMMON_DATA_DATE_RANGED = 'EVENTS_COMMON_DATA_DATE_RANGED',
  EVENTS_COMMON_DATA_TYPED_DATE_RANGED = 'EVENTS_COMMON_DATA_TYPED_DATE_RANGED',
  EVENTS_COMMON_DATA_COORDINATES = 'EVENTS_COMMON_DATA_COORDINATES',
  EVENTS_COMMON_DATA_ADDRESS = 'EVENTS_COMMON_DATA_ADDRESS',
  EVENTS_COMMON_DATA_TIMEINTERVAL = 'EVENTS_COMMON_DATA_TIMEINTERVAL',
  EVENTS_COMMON_DATA_CARRIER = 'EVENTS_COMMON_DATA_CARRIER',
  TRIP_CANCELLATION_CANCELLATION_INFO = 'TRIP_CANCELLATION_CANCELLATION_INFO',
  EVENTS_DELIVERY = 'EVENTS_DELIVERY',
  EVENTS_DELIVERY_CHECKIN = 'EVENTS_DELIVERY_CHECKIN',
  EVENTS_DELIVERY_CHECKOUT = 'EVENTS_DELIVERY_CHECKOUT',
  EVENTS_DELIVERY_ETA_UPDATE = 'EVENTS_DELIVERY_ETA_UPDATE',
  EVENTS_TRIP_SHIPMENT = 'EVENTS_TRIP_SHIPMENT',
  EVENTS_TRIP_SHIPMENT_ITEMS = 'EVENTS_TRIP_SHIPMENT_ITEMS',
  EVENTS_TRIP_LOCATION_CONTACT_DETAILS = 'EVENTS_TRIP_LOCATION_CONTACT_DETAILS',
  EVENTS_TRIP_TRAILER_DETAILS = 'EVENTS_TRIP_TRAILER_DETAILS',
  EVENTS_DELIVERY_BOL_UPDATE = 'EVENTS_DELIVERY_BOL_UPDATE',
  EVENTS_DELIVERY_TRIP_CREATE = 'EVENTS_DELIVERY_TRIP_CREATE',
  EVENTS_DELIVERY_TRIP_UPDATE = 'EVENTS_DELIVERY_TRIP_UPDATE',
  EVENTS_DELIVERY_TRIP_PATCH = 'EVENTS_DELIVERY_TRIP_PATCH',
  EVENTS_DELIVERY_TRIP_CLOSED = 'EVENTS_DELIVERY_TRIP_CLOSED',
  EVENTS_DELIVERY_TRIP_CANCEL = 'EVENTS_DELIVERY_TRIP_CANCEL',
  EVENTS_PGI = 'EVENTS_PGI',
  EVENTS_FAILED_COLLECTION = 'EVENTS_FAILED_COLLECTION',
  EVENTS_REVERSE_CHECKIN = 'EVENTS_REVERSE_CHECKIN',
}

export enum DocumentationTab {
  TAB_OBJECTIVE = 'TAB_OBJECTIVE',
  TAB_INTERFACE = 'TAB_INTERFACE',
  TAB_CALLBACK_URL = 'TAB_CALLBACK_URL',
  TAB_RETRY_POLICY = 'TAB_RETRY_POLICY',
  TAB_EVENTS = 'TAB_EVENTS'
}
